import { Motor, Battery, Propeller, SystemConfig, AnalysisResult } from '../types';

export const calculateBasicMetrics = (config: SystemConfig) => {
  const motorCount = 2; // 固定为2个电机
  const minThrust = config.targetWeight * 1000 * config.thrustRatio;
  const singleMotorThrust = minThrust / motorCount;
  
  return {
    minThrust: Math.round(minThrust),
    singleMotorThrust: Math.round(singleMotorThrust),
    motorCount
  };
};

export const calculateCombinationPerformance = (
  motor: Motor,
  battery: Battery,
  propeller: Propeller,
  config: SystemConfig,
  index: number
): AnalysisResult => {
  const motorCount = 2;
  const totalThrust = motor.maxThrust * motorCount;
  const thrustMargin = ((totalThrust / (config.targetWeight * 1000)) - 1) * 100;

  // 更精确的功率计算
  const motorEfficiencyAtHover = 0.75; // 悬停时电机效率
  const hoverThrust = (config.targetWeight * 1000) / motorCount; // 单电机悬停推力
  const thrustRatio = hoverThrust / motor.maxThrust; // 推力使用比例

  // 基于推力比例计算悬停功率
  const hoverPowerPerMotor = motor.maxPower * Math.pow(thrustRatio, 1.5) / motorEfficiencyAtHover;
  const totalMotorPower = hoverPowerPerMotor * motorCount;
  const systemPower = totalMotorPower + config.otherPower;

  // 计算续航时间
  const endurance = (battery.energy / systemPower) * 60; // 分钟

  // 计算总重量
  const totalWeight = (motor.weight * motorCount) + battery.weight + (propeller.weight * motorCount) + 350; // +350g机架及其他

  // 系统效率指标
  const thrustToWeightRatio = totalThrust / totalWeight;
  const powerEfficiency = totalThrust / systemPower; // g/W
  const efficiency = thrustToWeightRatio * powerEfficiency / 10;

  // ESC规格建议
  const escCurrent = Math.ceil(motor.maxCurrent * thrustRatio * 1.3); // 30%安全余量
  const escSpec = escCurrent + 'A';

  // 总成本
  const totalCost = (motor.price * motorCount) + battery.price + (propeller.price * motorCount) + 300; // +300其他组件

  // 综合评分算法
  let score = 0;

  // 续航时间评分 (0-30分)
  if (endurance >= 30) score += 30;
  else if (endurance >= 25) score += 25 + (endurance - 25);
  else if (endurance >= 18) score += 18 + (endurance - 18) * 0.8;
  else score += endurance * 0.6;

  // 推力冗余评分 (0-25分)
  if (thrustMargin >= 100) score += 25;
  else if (thrustMargin >= 80) score += 22 + (thrustMargin - 80) * 0.15;
  else if (thrustMargin >= 50) score += 15 + (thrustMargin - 50) * 0.23;
  else score += thrustMargin * 0.3;

  // 重量控制评分 (0-20分)
  if (totalWeight <= 1200) score += 20;
  else if (totalWeight <= 1400) score += 15 + (1400 - totalWeight) * 0.025;
  else if (totalWeight <= 1600) score += 10 + (1600 - totalWeight) * 0.025;
  else score += Math.max(0, 5 - (totalWeight - 1600) * 0.01);

  // 系统效率评分 (0-15分)
  if (efficiency >= 2.0) score += 15;
  else if (efficiency >= 1.5) score += 10 + (efficiency - 1.5) * 10;
  else score += efficiency * 6.67;

  // 成本控制评分 (0-10分)
  if (totalCost <= 2000) score += 10;
  else if (totalCost <= 3000) score += 7 + (3000 - totalCost) * 0.003;
  else score += Math.max(0, 5 - (totalCost - 3000) * 0.001);

  return {
    index,
    motor: motor.brand + ' ' + motor.model,
    battery: battery.brand + ' ' + battery.model,
    propeller: propeller.spec + ' (' + propeller.blades + '叶)',
    totalThrust: Math.round(totalThrust),
    thrustMargin: thrustMargin.toFixed(1),
    totalWeight: Math.round(totalWeight),
    systemPower: Math.round(systemPower),
    endurance: endurance.toFixed(1),
    efficiency: efficiency.toFixed(2),
    escSpec: escSpec,
    totalCost: Math.round(totalCost),
    score: score.toFixed(1),
    // 存储原始数据用于排序
    _thrustMargin: thrustMargin,
    _endurance: endurance,
    _efficiency: efficiency,
    _totalWeight: totalWeight,
    _totalCost: totalCost,
    _score: score
  };
};

export const sortAnalysisResults = (results: AnalysisResult[], criteria: string): AnalysisResult[] => {
  const sortedResults = [...results];
  
  switch (criteria) {
    case 'endurance':
      return sortedResults.sort((a, b) => b._endurance - a._endurance);
    case 'efficiency':
      return sortedResults.sort((a, b) => b._efficiency - a._efficiency);
    case 'thrust-margin':
      return sortedResults.sort((a, b) => b._thrustMargin - a._thrustMargin);
    case 'cost':
      return sortedResults.sort((a, b) => a._totalCost - b._totalCost);
    default:
      return sortedResults.sort((a, b) => b._score - a._score);
  }
}; 